import { useState, useEffect } from "react";
import { useQueries } from "@tanstack/react-query";
import { ModuleLayout } from "../../layouts";
import {
  Button,
  TabNavigation,
  KpiCard,
  BarChart,
  ProgressBar,
  KpiSummaryCard,
  ActionBanner
} from "../../components";
import LineChart from "../../components/LineChart";
import DonutChart from "../../components/DonutChart";
import SimpleBarChart from "../../components/SimpleBarChart";
import MetricCard from "../../components/MetricCard";
import PercentageCard from "../../components/PercentageCard";
import HighlightMetricCard from "../../components/HighlightMetricCard";
import SimpleMetricCard from "../../components/SimpleMetricCard";
import { useTranslation } from "react-i18next";
import { QUERY_CONSTANTS } from "../../constants";
import { setToStorage, getFromStorage } from "../../utils";
import {
  getPipelineMetricsApi,
  getProjectSuccessRateApi,
  getQualityScoreApi,
  getDefectDensityApi,
  getRevenueGrowthApi,
  getCustomerAcquisitionApi,
  getMarketShareApi,
  getCostSavingsApi,
  getCustomerSatisfactionApi,
  getMarketTrendsApi,
  getProcessOptimizationApi,
  getResourceUtilizationApi,
  getSummaryRevenueApi,
  getSummaryProjectsApi,
  getSummaryTeamApi,
  getSummaryQualityApi,
  getSummaryCustomersApi,
  getSummaryEfficiencyApi,
} from "../../DAL";

const Dashboard = () => {
  const { t } = useTranslation();
  const userName = "Zunain";
  const [activeTab, setActiveTab] = useState(() => {
    return getFromStorage('dashboardActiveTab') || "pipeline";
  });

  // Fetch all data using React Query
  const queries = useQueries({
    queries: [
      {
        queryKey: [QUERY_CONSTANTS.PIPELINE_METRICS],
        queryFn: async () => {
          const res = await getPipelineMetricsApi();
          return res?.data || null;
        },
      },
      {
        queryKey: [QUERY_CONSTANTS.PROJECT_SUCCESS_RATE],
        queryFn: async () => {
          const res = await getProjectSuccessRateApi();
          return res?.data || null;
        },
      },
      {
        queryKey: [QUERY_CONSTANTS.QUALITY_SCORE],
        queryFn: async () => {
          const res = await getQualityScoreApi();
          return res?.data || null;
        },
      },
      {
        queryKey: [QUERY_CONSTANTS.DEFECT_DENSITY],
        queryFn: async () => {
          const res = await getDefectDensityApi();
          return res?.data || null;
        },
      },
      {
        queryKey: [QUERY_CONSTANTS.REVENUE_GROWTH],
        queryFn: async () => {
          const res = await getRevenueGrowthApi();
          return res?.data || null;
        },
      },
      {
        queryKey: [QUERY_CONSTANTS.CUSTOMER_ACQUISITION],
        queryFn: async () => {
          const res = await getCustomerAcquisitionApi();
          return res?.data || null;
        },
      },
      {
        queryKey: [QUERY_CONSTANTS.MARKET_SHARE],
        queryFn: async () => {
          const res = await getMarketShareApi();
          return res?.data || null;
        },
      },
      {
        queryKey: [QUERY_CONSTANTS.COST_SAVINGS],
        queryFn: async () => {
          const res = await getCostSavingsApi();
          return res?.data || null;
        },
      },
      {
        queryKey: [QUERY_CONSTANTS.CUSTOMER_SATISFACTION],
        queryFn: async () => {
          const res = await getCustomerSatisfactionApi();
          return res?.data || null;
        },
      },
      {
        queryKey: [QUERY_CONSTANTS.MARKET_TRENDS],
        queryFn: async () => {
          const res = await getMarketTrendsApi();
          return res?.data || null;
        },
      },
      {
        queryKey: [QUERY_CONSTANTS.PROCESS_OPTIMIZATION],
        queryFn: async () => {
          const res = await getProcessOptimizationApi();
          return res?.data || null;
        },
      },
      {
        queryKey: [QUERY_CONSTANTS.RESOURCE_UTILIZATION],
        queryFn: async () => {
          const res = await getResourceUtilizationApi();
          return res?.data || null;
        },
      },
      {
        queryKey: [QUERY_CONSTANTS.SUMMARY_REVENUE],
        queryFn: async () => {
          const res = await getSummaryRevenueApi();
          return res?.data || null;
        },
      },
      {
        queryKey: [QUERY_CONSTANTS.SUMMARY_PROJECTS],
        queryFn: async () => {
          const res = await getSummaryProjectsApi();
          return res?.data || null;
        },
      },
      {
        queryKey: [QUERY_CONSTANTS.SUMMARY_TEAM],
        queryFn: async () => {
          const res = await getSummaryTeamApi();
          return res?.data || null;
        },
      },
      {
        queryKey: [QUERY_CONSTANTS.SUMMARY_QUALITY],
        queryFn: async () => {
          const res = await getSummaryQualityApi();
          return res?.data || null;
        },
      },
      {
        queryKey: [QUERY_CONSTANTS.SUMMARY_CUSTOMERS],
        queryFn: async () => {
          const res = await getSummaryCustomersApi();
          return res?.data || null;
        },
      },
      {
        queryKey: [QUERY_CONSTANTS.SUMMARY_EFFICIENCY],
        queryFn: async () => {
          const res = await getSummaryEfficiencyApi();
          return res?.data || null;
        },
      },
    ],
  });

  // Extract data from queries with loading and error states
  const [
    { data: pipelineData, isLoading: pipelineLoading, error: pipelineError },
    { data: projectSuccessData, isLoading: projectSuccessLoading },
    { data: qualityScoreData, isLoading: qualityScoreLoading },
    { data: defectDensityData, isLoading: defectDensityLoading },
    { data: revenueGrowthData, isLoading: revenueGrowthLoading, error: revenueGrowthError },
    { data: customerAcquisitionData, isLoading: customerAcquisitionLoading, error: customerAcquisitionError },
    { data: marketShareData, isLoading: marketShareLoading, error: marketShareError },
    { data: costSavingsData, isLoading: costSavingsLoading },
    { data: customerSatisfactionData, isLoading: customerSatisfactionLoading },
    { data: marketTrendsData, isLoading: marketTrendsLoading },
    { data: processOptimizationData, isLoading: processOptimizationLoading },
    { data: resourceUtilizationData, isLoading: resourceUtilizationLoading },
    { data: summaryRevenue },
    { data: summaryProjects },
    { data: summaryTeam },
    { data: summaryQuality },
    { data: summaryCustomers },
    { data: summaryEfficiency },
  ] = queries;

  const summaryData = {
    revenue: summaryRevenue,
    projects: summaryProjects,
    team: summaryTeam,
    quality: summaryQuality,
    customers: summaryCustomers,
    efficiency: summaryEfficiency,
  };

  useEffect(() => {
    setToStorage('dashboardActiveTab', activeTab);
  }, [activeTab]);

  const tabs = [
    { id: "pipeline", label: t("dashboard.tabPipeline") },
    { id: "success", label: t("dashboard.tabSuccess") },
    { id: "business", label: t("dashboard.tabBusiness") },
    { id: "customer", label: t("dashboard.tabCustomer") },
    { id: "process", label: t("dashboard.tabProcess") },
    { id: "more", label: t("dashboard.tabMore") },
  ];

  const kpiSummaryData = [
    {
      id: 1,
      title: t("dashboard.kpiRevenue"),
      subtitle: summaryData.revenue ? `${t("dashboard.kpiTotal")} $${(summaryData.revenue.total_revenue / 1000000).toFixed(1)}M` : t("dashboard.loadingState"),
      target: summaryData.revenue ? `${t("dashboard.kpiGrowth")} ${summaryData.revenue.growth_rate}%` : t("dashboard.loadingState"),
    },
    {
      id: 2,
      title: t("dashboard.kpiProjects"),
      subtitle: summaryData.projects ? `${t("dashboard.kpiActive")} ${summaryData.projects.active}` : t("dashboard.loadingState"),
      target: summaryData.projects ? `${t("dashboard.kpiSuccessRate")} ${summaryData.projects.success_rate}%` : t("dashboard.loadingState"),
    },
    {
      id: 3,
      title: t("dashboard.kpiTeam"),
      subtitle: summaryData.team ? `${t("dashboard.kpiMembers")} ${summaryData.team.total_members}` : t("dashboard.loadingState"),
      target: summaryData.team ? `${t("dashboard.kpiUtilization")} ${summaryData.team.utilization_rate}%` : t("dashboard.loadingState"),
    },
    {
      id: 4,
      title: t("dashboard.kpiQuality"),
      subtitle: summaryData.quality ? `${t("dashboard.kpiScore")} ${summaryData.quality.overall_score}%` : t("dashboard.loadingState"),
      target: summaryData.quality ? `${t("dashboard.kpiBugRate")} ${summaryData.quality.bug_rate}%` : t("dashboard.loadingState"),
    },
    {
      id: 5,
      title: t("dashboard.kpiCustomers"),
      subtitle: summaryData.customers ? `${t("dashboard.kpiTotal")} ${summaryData.customers.total_customers}` : t("dashboard.loadingState"),
      target: summaryData.customers ? `${t("dashboard.kpiRetention")} ${summaryData.customers.retention_rate}%` : t("dashboard.loadingState"),
    },
    {
      id: 6,
      title: t("dashboard.kpiEfficiency"),
      subtitle: summaryData.efficiency ? `${t("dashboard.kpiScore")} ${summaryData.efficiency.overall_efficiency}%` : t("dashboard.loadingState"),
      target: summaryData.efficiency ? `${t("dashboard.kpiAutomation")} ${summaryData.efficiency.automation_rate}%` : t("dashboard.loadingState"),
    },
  ];

  const handleKpiMenuClick = (id) => {
    console.log("Menu clicked for KPI:", id);
  };

  const handleGovernanceView = () => {
    console.log("Open governance view");
  };

  const handleExportReport = () => {
    console.log("Export quarterly report");
  };

  return (
    <ModuleLayout
      greeting={`${t("dashboard.greeting")}, ${userName}`}
      subtitle={t("dashboard.subtitle")}
      showProfile={false}
    >
        <div className="bg-white rounded-xl p-6 shadow-sm mb-6">
          <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-8 items-start">
            <div className="space-y-4">
              <div>
                <h2 className="text-2xl font-semibold text-black mb-1">
                  {t("dashboard.heroTitle1")}
                </h2>
                <h3 className="text-2xl font-semibold text-black mb-3">
                  {t("dashboard.heroTitle2")}
                </h3>
              </div>

              <p className="text-gray-500 text-sm leading-relaxed max-w-md">
                {t("dashboard.heroDescription")}
              </p>

              <div className="flex flex-wrap gap-3 pt-1">
                <Button variant="success" size="md">
                  {t("dashboard.btnCreateDashboard")}
                </Button>
                <Button variant="secondary" size="md">
                  {t("dashboard.btnImportData")}
                </Button>
              </div>
            </div>

            <div className="h-56">
              {revenueGrowthLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-gray-500">{t("dashboard.loadingState")}</div>
                </div>
              ) : revenueGrowthData?.historical_data ? (
                <LineChart
                  data={revenueGrowthData.historical_data.map(item => ({
                    name: item.period || item.name,
                    value: parseFloat(((item.revenue || item.value || 0) / 1000000).toFixed(2))
                  }))}
                  dataKey="value"
                  xAxisKey="name"
                />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-gray-400 text-sm">{t("dashboard.noRevenueData")}</div>
                </div>
              )}
            </div>
          </div>
        </div>

        <TabNavigation tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />

        {activeTab === "pipeline" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <KpiCard title={t("dashboard.cardPipelineStrength")}>
              {pipelineLoading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="text-gray-500">{t("dashboard.loadingState")}</div>
                </div>
              ) : pipelineError ? (
                <div className="flex items-center justify-center h-64">
                  <div className="text-red-500">Error: {pipelineError.message}</div>
                </div>
              ) : pipelineData?.pipeline_stages ? (
                <BarChart data={pipelineData.pipeline_stages} />
              ) : (
                <div className="flex items-center justify-center h-64">
                  <div className="text-gray-400 text-sm">{t("dashboard.noPipelineData")}</div>
                </div>
              )}
            </KpiCard>

            <KpiCard title={t("dashboard.cardPipelineVelocity")}>
              {pipelineLoading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="text-gray-500">{t("dashboard.loadingState")}</div>
                </div>
              ) : pipelineData ? (
                <ProgressBar
                  value={pipelineData.average_duration_days || 0}
                  maxValue={120}
                  label={t("dashboard.progressTarget")}
                  sublabel={t("dashboard.progressSublabel")}
                  color="#3B82F6"
                />
              ) : (
                <div className="flex items-center justify-center h-64">
                  <div className="text-gray-500">{t("dashboard.noDataState")}</div>
                </div>
              )}
            </KpiCard>
          </div>
        )}

        {activeTab === "success" && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
              <KpiCard title={t("dashboard.cardSuccessDistribution")}>
                {projectSuccessData ? (
                  <div className="h-64">
                    <DonutChart data={[
                      { name: t("dashboard.chartSuccessful"), value: projectSuccessData.successful_projects },
                      { name: t("dashboard.chartFailed"), value: projectSuccessData.failed_projects },
                      { name: t("dashboard.chartOnTime"), value: Math.round(projectSuccessData.total_projects * projectSuccessData.on_time_delivery / 100) },
                      { name: t("dashboard.chartWithinBudget"), value: Math.round(projectSuccessData.total_projects * projectSuccessData.within_budget / 100) },
                    ]} />
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-64">{t("dashboard.loadingState")}</div>
                )}
              </KpiCard>

              <KpiCard title={t("dashboard.cardQualityMetrics")}>
                {qualityScoreData ? (
                  <div className="h-64">
                    <SimpleBarChart data={[
                      { name: t("dashboard.chartCode"), value: qualityScoreData.code_quality },
                      { name: t("dashboard.chartDesign"), value: qualityScoreData.design_quality },
                      { name: t("dashboard.chartDocs"), value: qualityScoreData.documentation_quality },
                      { name: t("dashboard.chartTesting"), value: qualityScoreData.testing_quality },
                    ]} />
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-64">{t("dashboard.loadingState")}</div>
                )}
              </KpiCard>

              <KpiCard title={t("dashboard.cardDefectAnalysis")}>
                {defectDensityData ? (
                  <div className="h-64">
                    <SimpleBarChart data={[
                      { name: t("dashboard.chartCritical"), value: defectDensityData.critical_defects },
                      { name: t("dashboard.chartMajor"), value: defectDensityData.major_defects },
                      { name: t("dashboard.chartMinor"), value: defectDensityData.minor_defects },
                    ]} />
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-64">{t("dashboard.loadingState")}</div>
                )}
              </KpiCard>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <PercentageCard
                percentage={projectSuccessData ? `${projectSuccessData.success_rate}%` : "..."}
                title={t("dashboard.metricProjectSuccessRate")}
                subtitle={projectSuccessData ? `${projectSuccessData.successful_projects}/${projectSuccessData.total_projects} ${t("dashboard.metricProjects")}` : t("dashboard.loadingState")}
              />

              <MetricCard
                value={qualityScoreData ? `${qualityScoreData.overall_score}%` : "..."}
                label={t("dashboard.metricOverallQuality")}
                subtitle={qualityScoreData ? `${t("dashboard.chartCode")}: ${qualityScoreData.code_quality}%` : t("dashboard.loadingState")}
                iconColor="bg-blue-500"
              />

              <MetricCard
                value={defectDensityData ? defectDensityData.defects_per_kloc : "..."}
                label={t("dashboard.metricDefectsKloc")}
                subtitle={defectDensityData ? `${t("dashboard.metricTotal")} ${defectDensityData.total_defects}` : t("dashboard.loadingState")}
                iconColor="bg-red-500"
              />
            </div>
          </>
        )}

        {activeTab === "business" && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
              <KpiCard title={t("dashboard.cardRevenueGrowth")}>
                {revenueGrowthLoading ? (
                  <div className="flex items-center justify-center h-64">
                    <div className="text-gray-500">{t("dashboard.loadingState")}</div>
                  </div>
                ) : revenueGrowthError ? (
                  <div className="flex items-center justify-center h-64">
                    <div className="text-red-500 text-center">
                      <div>{t("dashboard.errorLoadingState")}</div>
                      <div className="text-xs mt-2">{revenueGrowthError.message}</div>
                    </div>
                  </div>
                ) : revenueGrowthData?.historical_data ? (
                  <div className="h-64">
                    <LineChart
                      data={revenueGrowthData.historical_data.map(item => ({
                        name: item.period || item.name,
                        value: parseFloat(((item.revenue || item.value || 0) / 1000000).toFixed(2))
                      }))}
                      dataKey="value"
                      xAxisKey="name"
                    />
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-64">
                    <div className="text-gray-400 text-sm">{t("dashboard.noRevenueData")}</div>
                  </div>
                )}
              </KpiCard>

              <KpiCard title={t("dashboard.cardCustomerAcquisition")}>
                {customerAcquisitionLoading ? (
                  <div className="flex items-center justify-center h-64">
                    <div className="text-gray-500">{t("dashboard.loadingState")}</div>
                  </div>
                ) : customerAcquisitionError ? (
                  <div className="flex items-center justify-center h-64">
                    <div className="text-red-500 text-center">
                      <div>{t("dashboard.errorLoadingState")}</div>
                      <div className="text-xs mt-2">{customerAcquisitionError.message}</div>
                    </div>
                  </div>
                ) : customerAcquisitionData?.quarterly_data ? (
                  <div className="h-64">
                    <LineChart
                      data={customerAcquisitionData.quarterly_data.map(item => ({
                        name: item.period || item.name,
                        value: item.customers || item.value || 0
                      }))}
                      dataKey="value"
                      xAxisKey="name"
                    />
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-64">
                    <div className="text-gray-400 text-sm">{t("dashboard.noCustomerData")}</div>
                  </div>
                )}
              </KpiCard>

              <KpiCard title={t("dashboard.cardMarketShare")}>
                {marketShareLoading ? (
                  <div className="flex items-center justify-center h-64">
                    <div className="text-gray-500">{t("dashboard.loadingState")}</div>
                  </div>
                ) : marketShareError ? (
                  <div className="flex items-center justify-center h-64">
                    <div className="text-red-500 text-center">
                      <div>{t("dashboard.errorLoadingState")}</div>
                      <div className="text-xs mt-2">{marketShareError.message}</div>
                    </div>
                  </div>
                ) : marketShareData?.historical_data ? (
                  <div className="h-64">
                    <LineChart
                      data={marketShareData.historical_data.map(item => ({
                        name: item.period || item.name,
                        value: parseFloat((item.market_share || item.value || 0).toFixed(2))
                      }))}
                      dataKey="value"
                      xAxisKey="name"
                    />
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-64">
                    <div className="text-gray-400 text-sm">{t("dashboard.noMarketShareData")}</div>
                  </div>
                )}
              </KpiCard>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <MetricCard
                value={revenueGrowthData ? `${revenueGrowthData.growth_percentage}%` : "..."}
                label={t("dashboard.metricRevenueGrowth")}
                subtitle={revenueGrowthData ? `$${(revenueGrowthData.growth_amount / 1000).toFixed(0)}K ${t("dashboard.metricKIncrease")}` : t("dashboard.loadingState")}
                iconColor="bg-blue-500"
                showArrow={false}
              />

              <MetricCard
                value={costSavingsData ? `$${(costSavingsData.total_savings / 1000000).toFixed(1)}M` : "..."}
                label={t("dashboard.metricCostSavings")}
                subtitle={costSavingsData ? `${costSavingsData.savings_percentage}% ${t("dashboard.metricReduction")}` : t("dashboard.loadingState")}
                iconColor="bg-red-500"
                showArrow={false}
              />

              <MetricCard
                value={marketShareData ? `${marketShareData.market_share_percentage}%` : "..."}
                label={t("dashboard.metricMarketShare")}
                subtitle={marketShareData ? `${t("dashboard.metricPosition")}${marketShareData.market_position}` : t("dashboard.loadingState")}
                iconColor="bg-orange-500"
                showArrow={false}
              />

              <MetricCard
                value={customerAcquisitionData ? `${customerAcquisitionData.conversion_rate}%` : "..."}
                label={t("dashboard.metricConversionRate")}
                subtitle={customerAcquisitionData ? `${customerAcquisitionData.new_customers} ${t("dashboard.metricNewCustomers")}` : t("dashboard.loadingState")}
                iconColor="bg-teal-500"
                showArrow={false}
              />
            </div>
          </>
        )}

        {activeTab === "customer" && (
          <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-6">
            <KpiCard title={t("dashboard.cardCustomerMarket")}>
              {customerSatisfactionData ? (
                <div className="h-80">
                  <SimpleBarChart data={[
                    { name: t("dashboard.chartNPS"), value: customerSatisfactionData.nps_score },
                    { name: t("dashboard.chartCSAT"), value: customerSatisfactionData.csat_score },
                    { name: t("dashboard.chartResponseRate"), value: customerSatisfactionData.response_rate },
                  ]} />
                </div>
              ) : (
                <div className="flex items-center justify-center h-80">{t("dashboard.loadingState")}</div>
              )}
            </KpiCard>

            <HighlightMetricCard
              value={customerSatisfactionData ? customerSatisfactionData.overall_satisfaction.toFixed(1) : "..."}
              label={t("dashboard.metricCustomerSatisfaction")}
              subtitle={customerSatisfactionData ? `${t("dashboard.metricNPS")} ${customerSatisfactionData.nps_score}` : t("dashboard.loadingState")}
              bgColor="bg-[#D2873E]"
            />
          </div>
        )}

        {activeTab === "process" && (
          <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-6">
            <KpiCard title={t("dashboard.cardResourceUtilization")}>
              {resourceUtilizationData ? (
                <div className="h-80">
                  <SimpleBarChart data={[
                    { name: t("dashboard.chartHumanResources"), value: resourceUtilizationData.human_resources },
                    { name: t("dashboard.chartInfrastructure"), value: resourceUtilizationData.infrastructure },
                    { name: t("dashboard.chartBudget"), value: resourceUtilizationData.budget_utilization },
                  ]} />
                </div>
              ) : (
                <div className="flex items-center justify-center h-80">{t("dashboard.loadingState")}</div>
              )}
            </KpiCard>

            <div className="space-y-6">
              <SimpleMetricCard
                value={processOptimizationData ? `${processOptimizationData.optimization_score}%` : "..."}
                label={t("dashboard.metricProcessOptimization")}
                subtitle={processOptimizationData ? `${processOptimizationData.processes_optimized}/${processOptimizationData.total_processes} ${t("dashboard.metricOptimized")}` : t("dashboard.loadingState")}
                iconColor="bg-red-500"
              />

              <SimpleMetricCard
                value={resourceUtilizationData ? `${resourceUtilizationData.overall_utilization}%` : "..."}
                label={t("dashboard.metricResourceUtilization")}
                subtitle={resourceUtilizationData ? `${resourceUtilizationData.capacity_available}% ${t("dashboard.metricCapacityAvailable")}` : t("dashboard.loadingState")}
                iconColor="bg-blue-500"
              />
            </div>
          </div>
        )}

        <div className="mt-8">
          <h2 className="text-lg font-semibold text-black mb-4">{t("dashboard.summaryTitle")}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {kpiSummaryData.map((kpi) => (
              <KpiSummaryCard
                key={kpi.id}
                title={kpi.title}
                subtitle={kpi.subtitle}
                target={kpi.target}
                onMenuClick={() => handleKpiMenuClick(kpi.id)}
              />
            ))}
          </div>
        </div>

        <div className="mt-6">
          <ActionBanner
            title={t("dashboard.actionBannerTitle")}
            subtitle={t("dashboard.actionBannerSubtitle")}
            primaryAction={{
              label: t("dashboard.actionGovernance"),
              onClick: handleGovernanceView,
            }}
            secondaryAction={{
              label: t("dashboard.actionExport"),
              onClick: handleExportReport,
            }}
          />
        </div>
    </ModuleLayout>
  );
};

export default Dashboard;
